/**
 * Promise-based controller for the discard confirm dialog: callers await
 * request(), the dialog resolves it. Provided by Builder.vue; harnesses
 * without the dialog fall back to window.confirm.
 */

import { inject, provide, ref } from 'vue';
import type { InjectionKey, Ref } from 'vue';

export interface DiscardConfirm {
    open: Ref<boolean>;
    request(): Promise<boolean>;
    resolve(result: boolean): void;
}

const DISCARD_CONFIRM_KEY: InjectionKey<DiscardConfirm> = Symbol('builder-v2-discard-confirm');

export function makeDiscardConfirm(): DiscardConfirm {
    const open = ref(false);
    let pending: ((result: boolean) => void) | null = null;

    return {
        open,
        request() {
            open.value = true;

            return new Promise<boolean>((resolve) => {
                pending = resolve;
            });
        },
        resolve(result) {
            open.value = false;
            pending?.(result);
            pending = null;
        },
    };
}

export function createDiscardConfirm(): DiscardConfirm {
    const confirm = makeDiscardConfirm();

    provide(DISCARD_CONFIRM_KEY, confirm);

    return confirm;
}

export function useDiscardConfirmOptional(): DiscardConfirm | null {
    return inject(DISCARD_CONFIRM_KEY, null);
}

export function useDiscardConfirm(): DiscardConfirm {
    const confirm = useDiscardConfirmOptional();

    if (!confirm) {
        throw new Error('useDiscardConfirm must be used inside a Builder tree');
    }

    return confirm;
}

const AGE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
];

/** "před 5 minutami" from an ISO/SQL timestamp; null when unparsable. */
export function relativeAge(timestamp: string, locale: string, now: Date = new Date()): string | null {
    const then = new Date(timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T'));

    if (Number.isNaN(then.getTime())) {
        return null;
    }

    const seconds = Math.round((then.getTime() - now.getTime()) / 1000);
    const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

    for (const [unit, size] of AGE_UNITS) {
        if (Math.abs(seconds) >= size) {
            return format.format(Math.trunc(seconds / size), unit);
        }
    }

    return format.format(seconds, 'second');
}
